import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import "./GlobalPostDialog.css";
import { FaPaperPlane } from "react-icons/fa";
import EmojiPicker from "emoji-picker-react";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { Grid } from "@giphy/react-components";
import { X } from "lucide-react"; // at the top

// ✅ Giphy API key
const gf = new GiphyFetch("4OQsubW6vV4uAMHoGaiK8VRRavmxEvYW");

const PostDialog = ({ isOpen, onClose, onSubmit }) => {
  const [postContent, setPostContent] = useState("");
  const [charLimit, setCharLimit] = useState(500); // Default fallback
  const [charRemaining, setCharRemaining] = useState(500);
  const textInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState("");

  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // ✅ Fetch char limit on mount
  useEffect(() => {
    if (isOpen) {
      const user = JSON.parse(localStorage.getItem("currentUser"));
      const groupId = user?.group_id;
      if (groupId) {
        fetch(`${process.env.REACT_APP_API_URL}/api/group/${groupId}`)
          .then((res) => res.json())
          .then((data) => {
            setCharLimit(data.postLimit);
            setCharRemaining(data.postLimit);
          })
          .catch((err) => {
            console.error("❌ Failed to fetch group limits:", err);
            setCharLimit(500);
            setCharRemaining(500);
          });
      }
    }
  }, [isOpen]);

  // ✅ Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setPostContent("");
      setSelectedFile(null);
      setSelectedGif(null);
      setShowEmojiPicker(false);
      setShowGifPicker(false);
    }
  }, [isOpen]);

  // ✅ Toggle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [isOpen]);

  // ✅ Autofocus textarea
  useEffect(() => {
    if (isOpen && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [isOpen]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleGifSelect = (gifUrl) => {
    setSelectedGif(gifUrl);
    setShowGifPicker(false);
  };

  const handleEmojiClick = (emojiData) => {
    const newContent = postContent + emojiData.emoji;
    if (newContent.length <= charLimit) {
      setPostContent(newContent);
      setCharRemaining(charLimit - newContent.length);
    }
    setShowEmojiPicker(false);
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= charLimit) {
      setPostContent(value);
      setCharRemaining(charLimit - value.length);
    } else {
      setPostContent(value.slice(0, charLimit));
      setCharRemaining(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postContent.trim() && !selectedFile && !selectedGif) return;

    const currentUser = localStorage.getItem("currentUser")
      ? JSON.parse(localStorage.getItem("currentUser")).username
      : null;

    if (!currentUser) {
      alert("You must be logged in to post.");
      return;
    }

    let requestBody;
    let headers = { "Content-Type": "application/json" };

    if (selectedFile) {
      requestBody = new FormData();
      requestBody.append("content", postContent);
      requestBody.append("username", currentUser);
      requestBody.append("file", selectedFile);
      headers = {};
    } else {
      requestBody = JSON.stringify({
        content: postContent,
        username: currentUser,
        gif: selectedGif,
      });
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`, {
        method: "POST",
        headers,
        body: requestBody,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to submit post.");
      }

      console.log("✅ Post submitted successfully:", result);

      if (typeof window.updatePosts === "function") {
        window.updatePosts((prev) => [result, ...prev]);
      }

      if (typeof window.updateProfilePosts === "function") {
        window.updateProfilePosts((prev) => [result, ...prev]);
      }

      setPostContent("");
      setSelectedFile(null);
      setSelectedGif(null);
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      onClose();
    } catch (error) {
      console.error("❌ Error submitting post:", error);
      alert("Failed to submit post. Please check the console for errors.");
    }
  };

  if (!isOpen) return null;

  // ✅ Render via portal so overlay covers *entire app*
return ReactDOM.createPortal(
  <div
    className="global-post-overlay"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0,0,0,0.6)",   // solid dark overlay, no blur
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999
    }}
  >
    <div
      className="global-post-modal"
      style={{
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        maxWidth: "600px",
        width: "90%",
        padding: "20px",
        position: "relative"
      }}
    >
      <button className="global-post-close" onClick={onClose}>✖</button>


      <div style={{ position: "relative", width: "100%", marginTop: "50px" }}>
  <textarea
    ref={textInputRef}
    className="global-post-input"
    placeholder="What's on your mind?"
    value={postContent}
    onChange={handleTextChange}
    rows={3}
    style={{
      resize: "none",
      overflowY: "auto",
      width: "100%",
      minHeight: "80px",
      paddingBottom: "24px"
    }}
  />
  <div
    className="char-counter"
    style={{
      position: "absolute",
      bottom: "6px",
      right: "10px",
      fontSize: "12px",
      color: charRemaining <= 10 ? "red" : "#666",
      pointerEvents: "none",
      background: "white"
    }}
  >
    {charRemaining}
  </div>
</div>

      {charRemaining <= 30 && (
        <div
          className="char-counter"
          style={{
            fontSize: "12px",
            textAlign: "right",
            color: charRemaining <= 10 ? "red" : "#666",
            marginTop: "4px"
          }}
        >
          {charRemaining} characters remaining
        </div>
      )}

      {selectedGif && (
        <div className="gif-preview">
          <img src={selectedGif} alt="Selected GIF" />
          <button className="remove-gif-btn" onClick={() => setSelectedGif(null)}>✖ Remove GIF</button>
        </div>
      )}

      {selectedFile && (
        <div className="media-preview">
          {selectedFile.type.startsWith("image/") ? (
            <img src={URL.createObjectURL(selectedFile)} alt="Preview" />
          ) : selectedFile.type.startsWith("video/") ? (
            <video controls>
              <source src={URL.createObjectURL(selectedFile)} type={selectedFile.type} />
            </video>
          ) : null}
        </div>
      )}

      <div className="global-post-action-bar">
        <div className="action-buttons">
          <button className="action-btn" onClick={() => fileInputRef.current.click()}>📷</button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*,video/*,audio/*"
            onChange={handleFileSelect}
          />
          <button className="action-btn" onClick={() => setShowGifPicker(true)}>🎞</button>
          <button className="action-btn" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>😊</button>
        </div>
        <button className="post-btn" onClick={handleSubmit}>✉ Post</button>
      </div>

      {showGifPicker && (
        <div className="gif-picker-overlay" onClick={() => setShowGifPicker(false)}>
          <div className="gif-picker-container" onClick={(e) => e.stopPropagation()}>
            <div className="gif-picker-header">
              <input
                type="text"
                className="gif-search-box"
                placeholder="Search GIFs..."
                value={gifSearch}
                onChange={(e) => setGifSearch(e.target.value)}
              />
              <button className="close-gif-btn" onClick={() => setShowGifPicker(false)}>✖ Close</button>
            </div>
            <Grid
              fetchGifs={(offset) =>
                gifSearch
                  ? gf.search(gifSearch, { offset, limit: 10 })
                  : gf.trending({ offset, limit: 10 })
              }
              width={400}
              columns={3}
              gutter={6}
              onGifClick={(gif, event) => {
                event.preventDefault();
                event.stopPropagation();
                handleGifSelect(gif.images.fixed_height.url);
              }}
            />
          </div>
        </div>
      )}

      {showEmojiPicker && (
        <div ref={emojiPickerRef} className="emoji-picker-container">
          <EmojiPicker onEmojiClick={handleEmojiClick} />
        </div>
      )}
    </div>
  </div>,
  document.body
);

};

export default PostDialog;
